/**
 * 대리인(Agent) 관리 페이지 (M13 / F-3) — admin 전용.
 *
 * 기능 (F-3 범위):
 *   - 대리인 목록 테이블 (id 오름차순)
 *   - 필터: 비활성 포함 토글, 검색 (id / 공식명 / 키워드 부분 일치 — 클라사이드)
 *   - 행별 활성/비활성 토글 (PATCH is_active)
 *
 * 보호 정책 (서버 가드와 일치):
 *   - 79999 (UNKNOWN AGENT) 행의 편집/토글 버튼은 disabled.
 *   - 서버에서 400 등이 돌아오면 상단 actionError 영역에 메시지 노출.
 *
 * Create/Edit 모달은 F-4 에서 추가. 본 단계에서는 [+ 새 대리인] / [편집] 버튼이
 * disabled 상태로 placeholder 만 둔다.
 */

import { type ChangeEvent, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  Download,
  FlaskConical,
  Loader2,
  Pencil,
  Plus,
  PowerOff,
  ShieldAlert,
  Upload,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  type AgentOut,
  type AgentRulesFile,
  exportAgentRules,
  UNKNOWN_AGENT_ID,
} from "@/lib/api/agents";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/cn";

import AgentFormModal from "./AgentFormModal";
import AgentRulesImportModal from "./AgentRulesImportModal";
import AgentStatsModal from "./AgentStatsModal";
import MatchTestModal from "./MatchTestModal";
import { useAgents, useUpdateAgent } from "./useAgents";

function useFormatDate() {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "ko" ? "ko-KR" : "ja-JP";
  return (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
  };
}

function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium",
        active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500",
      )}
    >
      {active ? t("admin.agents.badges.active") : t("admin.agents.badges.inactive")}
    </span>
  );
}

function WithholdingBadge({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  if (!enabled) {
    return <span className="text-[11px] text-slate-300">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
      <Zap className="h-3 w-3" />
      {t("admin.agents.badges.withholding")}
    </span>
  );
}

/** id/official_name/keywords 에 검색어 부분 일치 (대소문자 무시). */
function matchesSearch(agent: AgentOut, query: string): boolean {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (agent.id.toLowerCase().includes(q)) return true;
  if (agent.official_name.toLowerCase().includes(q)) return true;
  return agent.keywords.some((k) => k.toLowerCase().includes(q));
}

export default function AgentsPage() {
  const { t } = useTranslation();
  const formatDate = useFormatDate();

  const [includeInactive, setIncludeInactive] = useState(true);
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAgentId, setBusyAgentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AgentOut | null>(null);
  const [matchTestOpen, setMatchTestOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importFile, setImportFile] = useState<AgentRulesFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useAgents({ includeInactive });

  const handleExportRules = async () => {
    setActionError(null);
    setExporting(true);
    try {
      const file = await exportAgentRules();
      const stamp = (file.exported_at ?? new Date().toISOString()).slice(0, 16).replace(/\D/g, "");
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(file, null, 2)], { type: "application/json" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `agent-rules_${file.source ?? "server"}_${stamp}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof ApiError ? err.detail : (err as Error).message;
      setActionError(t("admin.agents.rulesTransfer.exportFailed", { message }));
    } finally {
      setExporting(false);
    }
  };

  const handleRulesFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = ""; // 같은 파일을 다시 골라도 onChange 가 오도록
    if (!picked) return;
    setActionError(null);
    try {
      const parsed = JSON.parse(await picked.text()) as AgentRulesFile;
      if (!parsed || !Array.isArray(parsed.agents)) throw new Error("not a rules file");
      setImportFile(parsed);
    } catch {
      setActionError(t("admin.agents.rulesTransfer.invalidFile"));
    }
  };
  const updateMu = useUpdateAgent();

  const filtered = useMemo(
    () => (data ?? []).filter((a) => matchesSearch(a, search)),
    [data, search],
  );

  const handleToggleActive = (a: AgentOut) => {
    if (a.id === UNKNOWN_AGENT_ID) return;
    setActionError(null);
    setBusyAgentId(a.id);
    updateMu.mutate(
      { agentId: a.id, payload: { is_active: !a.is_active } },
      {
        onError: (err) => {
          setActionError(err instanceof ApiError ? err.detail : err.message);
        },
        onSettled: () => setBusyAgentId(null),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{t("admin.agents.title")}</h1>
          <p className="mt-0.5 text-xs text-slate-500">{t("admin.agents.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleExportRules()}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
            title={t("admin.agents.rulesTransfer.exportTitle")}
          >
            {exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {t("admin.agents.rulesTransfer.exportButton")}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            title={t("admin.agents.rulesTransfer.importTitle")}
          >
            <Upload className="h-3.5 w-3.5" />
            {t("admin.agents.rulesTransfer.importButton")}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            data-testid="agent-rules-file-input"
            onChange={(e) => void handleRulesFile(e)}
          />
          <button
            type="button"
            onClick={() => setStatsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            title={t("admin.agents.stats.buttonTitle")}
          >
            <BarChart3 className="h-3.5 w-3.5" />
            {t("admin.agents.stats.button")}
          </button>
          <button
            type="button"
            onClick={() => setMatchTestOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            title={t("admin.agents.matchTest.buttonTitle")}
          >
            <FlaskConical className="h-3.5 w-3.5" />
            {t("admin.agents.matchTest.button")}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("admin.agents.newAgent")}
          </button>
        </div>
      </div>

      {/* 필터 바 */}
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.agents.searchPlaceholder")}
          className="min-w-[240px] flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          {t("admin.agents.filters.includeInactive")}
        </label>
        <span className="ml-auto text-[11px] text-slate-400">
          {t("admin.agents.filters.count", {
            shown: filtered.length,
            total: data?.length ?? 0,
          })}
        </span>
      </div>

      {actionError ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {actionError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">{t("admin.agents.th.id")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.officialName")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.currency")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.priority")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.withholding")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.keywords")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.status")}</th>
              <th className="px-3 py-2">{t("admin.agents.th.updatedAt")}</th>
              <th className="px-3 py-2 text-right">{t("admin.agents.th.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-xs text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-xs text-red-500">
                  {t("admin.agents.error", { message: error.message })}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-xs text-slate-400">
                  {search
                    ? t("admin.agents.emptyForSearch", { query: search })
                    : t("admin.agents.empty")}
                </td>
              </tr>
            ) : (
              filtered.map((a) => {
                const isUnknown = a.id === UNKNOWN_AGENT_ID;
                const isBusy = busyAgentId === a.id;
                return (
                  <tr
                    key={a.id}
                    className={cn(
                      "text-slate-700",
                      isUnknown && "bg-slate-50/60",
                      !a.is_active && "opacity-60",
                    )}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs">{a.id}</span>
                        {isUnknown ? (
                          <span
                            className="inline-flex items-center gap-0.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600"
                            title={t("admin.agents.badges.unknownTitle")}
                          >
                            <ShieldAlert className="h-2.5 w-2.5" />
                            {t("admin.agents.badges.unknown")}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium">{a.official_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {a.currency ? (
                        <span className="font-mono">{a.currency}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-slate-600">{a.priority}</td>
                    <td className="px-3 py-2">
                      <WithholdingBadge enabled={a.withholding_enabled} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {a.keywords.length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span title={a.keywords.join(", ")}>
                          {t("admin.agents.keywordsCount", { count: a.keywords.length })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <ActiveBadge active={a.is_active} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(a.updated_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditTarget(a)}
                          disabled={isUnknown}
                          title={
                            isUnknown
                              ? t("admin.agents.actions.unknownTitle")
                              : t("admin.agents.actions.editTitle")
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Pencil className="h-3 w-3" />
                          {t("admin.agents.actions.edit")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(a)}
                          disabled={isUnknown || isBusy}
                          title={
                            isUnknown
                              ? t("admin.agents.actions.unknownTitle")
                              : a.is_active
                                ? t("admin.agents.actions.deactivateTitle")
                                : t("admin.agents.actions.activateTitle")
                          }
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <PowerOff className="h-3 w-3" />
                          {a.is_active
                            ? t("admin.agents.actions.deactivateLabel")
                            : t("admin.agents.actions.activateLabel")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {matchTestOpen ? (
        <MatchTestModal
          onClose={() => setMatchTestOpen(false)}
          onSelectAgent={(agentId) => {
            const found = (data ?? []).find((a) => a.id === agentId);
            if (found) {
              setMatchTestOpen(false);
              setEditTarget(found);
            }
          }}
        />
      ) : null}
      {statsOpen ? (
        <AgentStatsModal
          onClose={() => setStatsOpen(false)}
          onSelectAgent={(agentId) => {
            const found = (data ?? []).find((a) => a.id === agentId);
            if (found) {
              setStatsOpen(false);
              setEditTarget(found);
            }
          }}
        />
      ) : null}
      {importFile ? (
        <AgentRulesImportModal file={importFile} onClose={() => setImportFile(null)} />
      ) : null}
      {createOpen ? <AgentFormModal mode="create" onClose={() => setCreateOpen(false)} /> : null}
      {editTarget ? (
        <AgentFormModal mode="edit" agent={editTarget} onClose={() => setEditTarget(null)} />
      ) : null}
    </div>
  );
}
